// Lab 4 deterministic scenarios: config query, group join, shard movement,
// requests during reconfiguration, duplicate transfers, single-group and
// cross-group transactions, abort with retry, the reconfig/transaction
// barrier, and coordinator/participant crash with recovery.
import { defaultConfig } from './model';

const INVOKE = (client, command) => ({ op: 'submit', to: client, command, label: 'invoke' });
const CMD = (client) => ({ op: 'deliver', select: { kind: 'clientCommand', to: client } });
const QUERY = (client) => ({ op: 'deliver', select: { kind: 'message', from: client, to: 'M', msgType: 'configquery' } });
const CONFIG = (client) => ({ op: 'deliver', select: { kind: 'message', from: 'M', to: client, msgType: 'config' } });
const ADMINQ = (client) => ({ op: 'deliver', select: { kind: 'message', from: client, to: 'M', msgType: 'admin' } });
const ADMINREPLY = (client) => ({ op: 'deliver', select: { kind: 'message', from: 'M', to: client, msgType: 'adminreply' } });
const ANN = (g) => ({ op: 'deliver', select: { kind: 'message', from: 'M', to: g, msgType: 'announce' } });
const SREQ = (t) => ({ op: 'deliver', select: { kind: 'message', to: t, msgType: 'srequest' } });
const SREP = () => ({ op: 'deliver', select: { kind: 'message', msgType: 'sreply' } });
const REJECT = () => ({ op: 'deliver', select: { kind: 'message', msgType: 'reject' } });
const MOVE = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'move' } });
const MOVEACK = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'moveack' } });
const TXN = (t) => ({ op: 'deliver', select: { kind: 'message', to: t, msgType: 'txn' } });
const TXNREPLY = () => ({ op: 'deliver', select: { kind: 'message', msgType: 'txnreply' } });
const PREP = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'prepare' } });
const PREPOK = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'prepareok' } });
const PREPABORT = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'prepareabort' } });
const COMMIT = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'commit' } });
const COMMITOK = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'commitok' } });
const TXNABORT = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'txnabort' } });
const ABORTOK = (f, t) => ({ op: 'deliver', select: { kind: 'message', from: f, to: t, msgType: 'abortok' } });
const RETRY = (client, seq) => ({ op: 'fire', select: { kind: 'timer', to: client, timerKey: 'retry:' + seq } });
const MOVET = (g, cfg, shard) => ({ op: 'fire', select: { kind: 'timer', to: g, timerKey: 'move:' + cfg + ':' + shard } });
const TXNT = (g, phase, txnId) => ({ op: 'fire', select: { kind: 'timer', to: g, timerKey: 'txn:' + phase + ':' + txnId } });

const PUT_X = { op: 'put', key: 'x', value: 1 };
const PUT_A = { op: 'put', key: 'a', value: 2 };
const GET_A = { op: 'get', key: 'a' };
const MULTI_AB = { op: 'multi', writes: [{ key: 'a', value: 10 }, { key: 'b', value: 20 }] };
const MULTI_XA = { op: 'multi', writes: [{ key: 'x', value: 11 }, { key: 'a', value: 21 }] };
const MULTI_XC = { op: 'multi', reads: ['a'], writes: [{ key: 'x', value: 12 }, { key: 'c', value: 22 }] };
const MULTI_GET_AB = { op: 'multi', reads: ['a', 'b'] };

// First contact: query the master, route one key, complete.
export function queryAndRoute() {
  return {
    config: defaultConfig(),
    seed: 1,
    decisions: [
      INVOKE('C1', PUT_X), CMD('C1'),
      QUERY('C1'), CONFIG('C1'),
      SREQ('G1'), SREP(),
    ],
  };
}

export function addGroup() {
  return {
    config: defaultConfig(),
    seed: 2,
    decisions: [
      INVOKE('C1', PUT_A), CMD('C1'),
      QUERY('C1'), CONFIG('C1'),
      SREQ('G1'), SREP(),
      INVOKE('C1', { op: 'admin', adminOp: 'join', group: 'G3' }), CMD('C1'),
      ADMINQ('C1'), ADMINREPLY('C1'),
      ANN('G1'), ANN('G2'), ANN('G3'),
      MOVE('G1', 'G3'), MOVEACK('G3', 'G1'),
    ],
  };
}

export function moveShard() {
  return {
    config: defaultConfig(),
    seed: 3,
    decisions: [
      INVOKE('C1', PUT_A), CMD('C1'),
      QUERY('C1'), CONFIG('C1'),
      SREQ('G1'), SREP(),
      INVOKE('C1', { op: 'admin', adminOp: 'move', shard: 1, group: 'G2' }), CMD('C1'),
      ADMINQ('C1'), ADMINREPLY('C1'),
      ANN('G1'), ANN('G2'), ANN('G3'),
      MOVE('G1', 'G2'), MOVEACK('G2', 'G1'),
      INVOKE('C1', GET_A), CMD('C1'),
      SREQ('G2'), SREP(),
    ],
  };
}

export function requestDuringReconfig() {
  return {
    config: { ...defaultConfig(), clients: ['C1', 'C2'] },
    seed: 4,
    decisions: [
      INVOKE('C2', PUT_X), CMD('C2'),
      QUERY('C2'), CONFIG('C2'),
      SREQ('G1'), SREP(),
      INVOKE('C1', { op: 'admin', adminOp: 'move', shard: 1, group: 'G2' }), CMD('C1'),
      ADMINQ('C1'), ADMINREPLY('C1'),
      ANN('G1'),
      INVOKE('C2', { op: 'put', key: 'x', value: 9 }), CMD('C2'),
      SREQ('G1'),
      REJECT(),
      QUERY('C2'), CONFIG('C2'),
      ANN('G2'), ANN('G3'),
      MOVE('G1', 'G2'), MOVEACK('G2', 'G1'),
      RETRY('C2', 2),
      SREQ('G1'), SREP(),
    ],
  };
}

export function duplicateTransfer() {
  return {
    config: defaultConfig(),
    seed: 5,
    decisions: [
      INVOKE('C1', PUT_A), CMD('C1'),
      QUERY('C1'), CONFIG('C1'),
      SREQ('G1'), SREP(),
      INVOKE('C1', { op: 'admin', adminOp: 'move', shard: 1, group: 'G2' }), CMD('C1'),
      ADMINQ('C1'), ADMINREPLY('C1'),
      ANN('G1'), ANN('G2'), ANN('G3'),
      MOVE('G1', 'G2'),
      { ...MOVEACK('G2', 'G1'), op: 'drop' },
      MOVET('G1', 2, 1),
      MOVE('G1', 'G2'),
      MOVEACK('G2', 'G1'),
    ],
  };
}

export function singleGroupTxn() {
  return {
    config: defaultConfig(),
    seed: 6,
    decisions: [
      INVOKE('C1', MULTI_XA), CMD('C1'),
      QUERY('C1'), CONFIG('C1'),
      TXN('G1'), TXNREPLY(),
    ],
  };
}

export function crossGroupTxn() {
  return {
    config: defaultConfig(),
    seed: 7,
    decisions: [
      INVOKE('C1', MULTI_AB), CMD('C1'),
      QUERY('C1'), CONFIG('C1'),
      TXN('G2'),
      { ...PREP('G2', 'G1'), op: 'drop' },
      TXNT('G2', 'prepare', 'C1#1#1'),
      PREP('G2', 'G1'), PREPOK('G1', 'G2'),
      COMMIT('G2', 'G1'), COMMITOK('G1', 'G2'),
      TXNREPLY(),
      INVOKE('C1', MULTI_GET_AB), CMD('C1'),
      TXN('G2'),
      PREP('G2', 'G1'), PREPOK('G1', 'G2'),
      COMMIT('G2', 'G1'), COMMITOK('G1', 'G2'),
      TXNREPLY(),
    ],
  };
}

export function abortAndRetry() {
  return {
    config: { ...defaultConfig(), clients: ['C1', 'C2'] },
    seed: 8,
    decisions: [
      INVOKE('C1', MULTI_AB), CMD('C1'),
      QUERY('C1'), CONFIG('C1'),
      TXN('G2'),
      PREP('G2', 'G1'),
      INVOKE('C2', MULTI_XC), CMD('C2'),
      QUERY('C2'), CONFIG('C2'),
      TXN('G2'),
      PREP('G2', 'G1'),
      PREPOK('G1', 'G2'),
      PREPABORT('G1', 'G2'),
      COMMIT('G2', 'G1'), COMMITOK('G1', 'G2'),
      TXNREPLY(),
      { ...TXNABORT('G2', 'G1'), op: 'drop' },
      TXNT('G2', 'abort', 'C2#1#1'),
      TXNABORT('G2', 'G1'), ABORTOK('G1', 'G2'),
      TXNREPLY(),
      TXN('G2'),
      PREP('G2', 'G1'), PREPOK('G1', 'G2'),
      COMMIT('G2', 'G1'), COMMITOK('G1', 'G2'),
      TXNREPLY(),
    ],
  };
}

export function reconfigDuringTxn() {
  return {
    config: { ...defaultConfig(), clients: ['C1', 'C2'] },
    seed: 9,
    decisions: [
      INVOKE('C1', MULTI_AB), CMD('C1'),
      QUERY('C1'), CONFIG('C1'),
      TXN('G2'),
      PREP('G2', 'G1'),
      INVOKE('C2', { op: 'admin', adminOp: 'move', shard: 3, group: 'G1' }), CMD('C2'),
      ADMINQ('C2'), ADMINREPLY('C2'),
      ANN('G1'), ANN('G2'), ANN('G3'),
      PREPOK('G1', 'G2'),
      COMMIT('G2', 'G1'), COMMITOK('G1', 'G2'),
      TXNREPLY(),
      MOVE('G2', 'G1'), MOVEACK('G1', 'G2'),
    ],
  };
}

export function coordinatorCrash() {
  return {
    config: defaultConfig(),
    seed: 10,
    decisions: [
      INVOKE('C1', MULTI_AB), CMD('C1'),
      QUERY('C1'), CONFIG('C1'),
      TXN('G2'),
      PREP('G2', 'G1'),
      { op: 'crash', node: 'G2' },
      { op: 'restart', node: 'G2' },
      PREPOK('G1', 'G2'),
      { ...COMMIT('G2', 'G1'), op: 'drop' },
      TXNT('G2', 'commit', 'C1#1#1'),
      COMMIT('G2', 'G1'), COMMITOK('G1', 'G2'),
      TXNREPLY(),
    ],
  };
}

// A participant that crashes after preparing still holds its locks on
// restart, so the coordinator's commit lands on the prepared state.
export function participantCrash() {
  return {
    config: defaultConfig(),
    seed: 11,
    decisions: [
      INVOKE('C1', MULTI_AB), CMD('C1'),
      QUERY('C1'), CONFIG('C1'),
      TXN('G2'),
      PREP('G2', 'G1'),
      PREPOK('G1', 'G2'),
      { op: 'crash', node: 'G1' },
      { op: 'restart', node: 'G1' },
      COMMIT('G2', 'G1'), COMMITOK('G1', 'G2'),
      TXNREPLY(),
    ],
  };
}

export const ALL_SCENARIOS = {
  queryAndRoute,
  addGroup,
  moveShard,
  requestDuringReconfig,
  duplicateTransfer,
  singleGroupTxn,
  crossGroupTxn,
  abortAndRetry,
  reconfigDuringTxn,
  coordinatorCrash,
  participantCrash,
};
